import React, {Fragment, useMemo, useState} from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import PublishIcon from "@mui/icons-material/Publish";
import {Box, Tooltip} from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {v4 as uuidv4} from "uuid";
import {showMessage, SNACKBAR_MESSAGE_SEVERITY_WARNING} from "../../hocs/snackbar-provider";
import {addSpinnerMessage, markSpinnerMessage} from "../../state/spinner/spinnerActions";

export const READ_AS_FILE = "file";
export const READ_AS_TEXT = "text";
export const READ_AS_DATA_URL = "data-url";

const FileInputLocal = ({label, value, onChange, disabled, accept, readAs = READ_AS_TEXT, maxFileSize, ...props}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const id = useMemo(() => uuidv4(), []);
  const [fileName, setFileName] = useState(null);

  return (
    <Fragment>
      <Box
        component="input"
        accept={accept}
        sx={{display: "none"}}
        id={id}
        type="file"
        onChange={e => {
          const uuid = uuidv4();
          dispatch(addSpinnerMessage(uuid, t("components.fileInputLocal.messages.upload.start")));

          const file = e.target.files[0];

          if (!file) {
            dispatch(markSpinnerMessage(uuid));
            return;
          }

          if (maxFileSize && file.size > maxFileSize) {
            showMessage(t("components.fileInputLocal.messages.upload.fileTooBig"), SNACKBAR_MESSAGE_SEVERITY_WARNING);
            dispatch(markSpinnerMessage(uuid));
            return;
          }

          const lastDotIndex = file.name.lastIndexOf(".");
          const hasExtension = lastDotIndex > 0 && lastDotIndex < file.name.length - 1; // lastDotIndex > 0 avoids counting the leading dot of hidden files as an extension separator.

          const fileName = hasExtension ? file.name.slice(0, lastDotIndex) : file.name;
          const fileExtension = hasExtension ? file.name.slice(lastDotIndex + 1) : "";

          setFileName(`${fileName}.${fileExtension}`);

          if (readAs === READ_AS_FILE) {
            onChange(file, fileName, fileExtension);
            dispatch(markSpinnerMessage(uuid));
          } else {
            const reader = new FileReader();

            reader.onload = e => {
              const content = e.target.result;
              onChange(content, fileName, fileExtension);
            };
            reader.onloadend = () => {
              dispatch(markSpinnerMessage(uuid));
            };

            if (readAs === READ_AS_DATA_URL) {
              reader.readAsDataURL(file);
            } else {
              reader.readAsText(file);
            }
          }
        }}
        disabled={disabled || !!value}
      />
      <TextField
        variant="outlined"
        label={label}
        value={fileName || ""}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment
              sx={{
                marginRight: theme => theme.spacing(2)
              }}
              position="start"
            >
              <Tooltip title={t("components.fileInputLocal.upload.title")}>
                <label htmlFor={id}>
                  <Button
                    startIcon={<PublishIcon />}
                    variant="outlined"
                    disabled={disabled || !!value}
                    component="span"
                  >
                    {t("components.fileInputLocal.upload.label")}
                  </Button>
                </label>
              </Tooltip>
            </InputAdornment>
          ),
          endAdornment: (value || "").length > 0 && (
            <InputAdornment position="end">
              <Tooltip title={t("components.fileInputLocal.reset.title")}>
                <IconButton
                  onClick={() => {
                    setFileName(null);
                    if (onChange) {
                      onChange(null, null, null);
                    }
                    const input = document.getElementById(id);
                    input.value = null;
                  }}
                  disabled={disabled}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )
        }}
        {...props}
      />
    </Fragment>
  );
};

export default FileInputLocal;
